import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

const Users = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [roleAccess, setRoleAccess] = useState("");
  const [status, setStatus] = useState("Active");
  const [approved, setApproved] = useState(false);

  // Error state
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    roleAccess: "",
  });

  // Sample users data
  const [users, setUsers] = useState([
    {
      id: 1,
      name: "Anderson",
      email: "Anderson@gmail.com",
      password: "password123",
      roleAccess: "HR",
      status: "Active",
      approved: true
    },
    {
      id: 2,
      name: "John Doe",
      email: "john@example.com",
      password: "password123",
      roleAccess: "Manager",
      status: "Active",
      approved: false
    }
  ]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      roleAccess: "",
    };

    // Name validation
    if (!name.trim()) {
      newErrors.name = "Name is required.";
      valid = false;
    } else if (name.length < 3) {
      newErrors.name = "Name must be at least 3 characters.";
      valid = false;
    }

    // Email validation
    if (!email.trim()) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address.";
      valid = false;
    }

    // Password validation (only required for new users or when password is changed)
    if ((!isEditing || password) && !password) {
      newErrors.password = "Password is required.";
      valid = false;
    } else if ((!isEditing || password) && password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
      valid = false;
    }

    // Confirm Password validation (only required when password is entered)
    if (password && !confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password.";
      valid = false;
    } else if (password && password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
      valid = false;
    }

    // Role Access validation
    if (!roleAccess) {
      newErrors.roleAccess = "Please select a role.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      if (isEditing) {
        // Update existing user
        setUsers(users.map(user => 
          user.id === currentUserId 
            ? { 
                ...user, 
                name, 
                email, 
                roleAccess, 
                status,
                approved,
                ...(password && { password }) // Only update password if changed
              } 
            : user
        ));
        setShowSuccess(true);
      } else {
        // Add new user
        const newUser = {
          id: users.length + 1,
          name,
          email,
          password,
          roleAccess,
          status: "Active",
          approved: false
        };
        setUsers([...users, newUser]);
        setShowSuccess(true);
      }
      
      // Close sidebar after submission
      setShowSidebar(false);
      
      // Reset form
      resetForm();

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
  };

  const handleEdit = (userId) => {
    const userToEdit = users.find(user => user.id === userId);
    if (userToEdit) {
      setName(userToEdit.name);
      setEmail(userToEdit.email);
      setPassword(""); // Clear password fields when editing
      setConfirmPassword(""); // Clear confirm password when editing
      setRoleAccess(userToEdit.roleAccess);
      setStatus(userToEdit.status);
      setApproved(userToEdit.approved);
      setIsEditing(true);
      setCurrentUserId(userId);
      setShowSidebar(true);
    }
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setRoleAccess("");
    setStatus("Active");
    setApproved(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setErrors({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      roleAccess: "",
    });
    setIsEditing(false);
    setCurrentUserId(null);
  };

  // Check for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.querySelector(".sidebar-container");
      if (showSidebar && sidebar && !sidebar.contains(event.target)) {
        const addButton = document.querySelector(".add-role-button");
        if (addButton && !addButton.contains(event.target)) {
          setShowSidebar(false);
          resetForm();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSidebar]);

  return (
    <main className="main-content relative">
      {/* Success Message Popup */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-[#635BFF] text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
            <span className="material-symbols-outlined mr-2">check_circle</span>
            {isEditing ? "User Udpated successfully!" : "User Created successfully!"}
          </div>
        </div>
      )}

      {/* Dark overlay when sidebar is open */}
      {showSidebar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={() => {
            setShowSidebar(false);
            resetForm();
          }}
        />
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${showSidebar ? "" : ""}`}>
        <div className="mb-6 flex flex-wrap items-center justify-between ml-4">
          <h2 className="text-xl font-semibold text-gray-800 ">Users</h2>

          <nav>
            <ol className="flex items-center gap-1.5 mr-7">
              <li>
                <Link
                  className="inline-flex items-center gap-1.5 text-sm text-black "
                  to="/dashboard"
                >
                  Home /
                </Link>
              </li>
              <li className="text-sm text-gray-800 ">Users</li>
            </ol>
          </nav>
        </div>

        <div className="mx-auto p-4 md:p-4">
          <div className="space-y-5 sm:space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white  ">
              <div className="px-5 py-4 sm:px-6 sm:py-5">
                <h3 className="text-base font-medium text-gray-800 ">
                  <div className="flex justify-end">
                    <button
                      className="add-role-button bg-[#635BFF] text-white px-4 py-2 rounded-lg shadow-lg hover:bg-[#4a28aa] transition"
                      onClick={() => {
                        setShowSidebar(true);
                        resetForm();
                      }}
                    >
                      Add Users
                    </button>
                  </div>
                </h3>
              </div>
              <div className="p-5 border-t border-gray-100  sm:p-6">
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white  ">
                  <div className="max-w-full overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr
                          className="text-white border-b border-gray-100  whitespace-nowrap"
                          style={{ backgroundColor: "#635BFF" }}
                        >
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                SI.No
                              </p>
                            </div>
                          </th>
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Name
                              </p>
                            </div>
                          </th>
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Email
                              </p>
                            </div>
                          </th>
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Status
                              </p>
                            </div>
                          </th>
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Approved
                              </p>
                            </div>
                          </th>
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Roles
                              </p>
                            </div>
                          </th>
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Action
                              </p>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {users.map((user, index) => (
                          <tr key={user.id} className="whitespace-nowrap">
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <p className="text-black text-sm ">{index + 1}</p>
                              </div>
                            </td>
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <p className="text-black text-sm ">{user.name}</p>
                              </div>
                            </td>
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <p className="text-black text-sm ">{user.email}</p>
                              </div>
                            </td>
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <button className={`rounded-full px-2 py-0.5 text-sm font-medium text-white ${
                                  user.status === "Active" ? "bg-[#047A48]" : "bg-[#DC2626]"
                                }`}>
                                  {user.status}
                                </button>
                              </div>
                            </td>
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={user.approved}
                                    readOnly
                                    className="checkbox"
                                  />
                                </label>
                              </div>
                            </td>
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <p className="text-black text-sm ">{user.roleAccess}</p>
                              </div>
                            </td>
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <button 
                                  className="rounded-full bg-[#635BFF] px-3 py-0.5 text-sm font-medium text-white"
                                  onClick={() => handleEdit(user.id)}
                                >
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar */}
      {showSidebar && (
        <div
          className={`sidebar-container fixed top-0 right-0 h-full bg-white shadow-lg z-50 transition-all duration-300 transform translate-x-0
            ${isMobile ? "w-full max-w-sm" : "w-96"}`}
        >
          <div className="h-full flex flex-col p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-800">
                {isEditing ? "Edit User" : "Add New User"}
              </h3>
              <button
                onClick={() => {
                  setShowSidebar(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <hr></hr>
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col mt-5">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 placeholder:text-sm ${
                    errors.name
                      ? "border-[#DC2626] focus:ring-[#DC2626]"
                      : "focus:ring-[#635BFF]"
                  }`}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors({...errors, name: ""});
                  }}
                  placeholder="Enter your name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email<span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 placeholder:text-sm ${
                    errors.email
                      ? "border-[#DC2626] focus:ring-[#DC2626]"
                      : "focus:ring-[#635BFF]"
                  }`}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({...errors, email: ""});
                  }}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Password Field (always shown, but optional when editing) */}
              <div className="mb-4 relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password{!isEditing && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 placeholder:text-sm ${
                      errors.password
                        ? "border-[#DC2626] focus:ring-[#DC2626]"
                        : "focus:ring-[#635BFF]"
                    }`}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) setErrors({...errors, password: ""});
                    }}
                    placeholder={isEditing ? "Leave blank to keep current" : "Enter your password"}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password Field (shown when password is entered or when creating new user) */}
              {(password || !isEditing) && (
                <div className="mb-4 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password{!isEditing && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 placeholder:text-sm ${
                        errors.confirmPassword
                          ? "border-[#DC2626] focus:ring-[#DC2626]"
                          : "focus:ring-[#635BFF]"
                      }`}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (errors.confirmPassword) setErrors({...errors, confirmPassword: ""});
                      }}
                      placeholder={isEditing ? "Leave blank to keep current" : "Confirm your password"}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-3 text-gray-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Access<span className="text-red-500">*</span>
                </label>
                <select
                  className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.roleAccess
                      ? "border-[#DC2626] focus:ring-[#DC2626]"
                      : "focus:ring-[#635BFF]"
                  }`}
                  value={roleAccess}
                  onChange={(e) => {
                    setRoleAccess(e.target.value);
                    if (errors.roleAccess) setErrors({...errors, roleAccess: ""});
                  }}
                >
                  <option value="">Select Role</option>
                  <option value="HR">HR</option>
                  <option value="Manager">Manager</option>
                  <option value="Accounts">Accounts</option>
                </select>
                {errors.roleAccess && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.roleAccess}
                  </p>
                )}
              </div>

              {isEditing && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#635BFF]"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Approved
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={approved}
                        onChange={(e) => setApproved(e.target.checked)}
                        className="checkbox"
                      />
                      <span className="ml-2">Approved</span>
                    </label>
                  </div>
                </>
              )}

              <div className="mt-auto">
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="w-full bg-[#635BFF] text-white py-2 px-4 rounded-md hover:bg-[#4a28aa] transition"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSidebar(false);
                      resetForm();
                    }}
                    className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default Users;